import { HelpCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip";
import { TOOLTIPS } from "@/lib/guidance/tooltips.config";
import { isStageEligible } from "@/lib/guidance/disclosure";
import type { TTooltipId, TTooltipTrigger } from "@/lib/guidance/types";
import { useGuidanceStage } from "@/hooks/useGuidanceStage";
import { useStore } from "@/store/store";

type TGuidanceTooltipProps = {
  guidanceId: TTooltipId;
  trigger?: TTooltipTrigger;
  children?: React.ReactNode;
};

export function GuidanceTooltip({ guidanceId, trigger = "firstVisit", children }: TGuidanceTooltipProps) {
  const { stage, isGuidanceHydrated } = useGuidanceStage();
  const isTooltipSeen = useStore((state) => state.isTooltipSeen);
  const markTooltipSeen = useStore((state) => state.markTooltipSeen);
  const emitGuidanceEvent = useStore((state) => state.emitGuidanceEvent);

  const definition = TOOLTIPS[guidanceId];
  const [open, setOpen] = useState(false);

  const isEligible =
    isGuidanceHydrated && isStageEligible(stage, definition.requiredStage) && !isTooltipSeen(guidanceId);

  useEffect(() => {
    if (trigger === "firstVisit" && isEligible) {
      setOpen(true);
      emitGuidanceEvent({ type: "tooltip", id: guidanceId, action: "shown" });
    }
  }, [emitGuidanceEvent, guidanceId, isEligible, trigger]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && isEligible) {
      markTooltipSeen(guidanceId);
    }
  };

  const handleInteraction = () => {
    if (trigger !== "firstInteraction" || !isEligible) {
      return;
    }
    setOpen(true);
    emitGuidanceEvent({ type: "tooltip", id: guidanceId, action: "shown" });
  };

  const handleDismiss = () => {
    markTooltipSeen(guidanceId);
    setOpen(false);
  };

  if (!isEligible) {
    return children ?? null;
  }

  return (
    <TooltipProvider>
      <Tooltip
        open={open}
        onOpenChange={handleOpenChange}
      >
        <TooltipTrigger
          asChild
          onPointerDown={trigger === "firstInteraction" ? handleInteraction : undefined}
          onFocus={trigger === "firstInteraction" ? handleInteraction : undefined}
        >
          {children ?? (
            <button
              type="button"
              className="inline-flex items-center text-muted-foreground hover:text-foreground"
              aria-label="Help"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-xs space-y-2 p-3"
        >
          <p className="font-medium text-foreground">{definition.title}</p>
          <p className="text-xs text-muted-foreground">{definition.content}</p>
          {trigger === "manual" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 w-full rounded-full"
              onClick={handleDismiss}
            >
              Got it
            </Button>
          ) : null}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
